import { STATUSPAGE } from '@repo/ui';
import {
  PushNotificationResponse,
  RecurringPushNotificationResponse,
  SchedulePushNotificationResponse,
} from 'domain/models';
import { GetAllOrganizationsUserParams } from 'domain/models/params/organization';
import { GetAllCoursesUserUseCaseInterface } from 'domain/services/courses/get-all-courses-user';
import {
  GetAllGroupsUserUseCaseInterface,
  GetAllOrganizationsUserUseCaseInterface,
  PushNotificationUseCaseInterface,
  RecurringPushNotificationUseCaseInterface,
  SchedulePushNotificationUseCaseInterface,
} from 'domain/usecases';
import {
  getPtBrRecurrenceType,
  RECURRENCE_TYPE,
} from 'domain/value-objects/recurrence-type';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import {
  loadCoursesUser,
  setLoadingCoursesUser,
} from 'presentation/config/store/courses/courses-slice';
import { loadGroupsUser } from 'presentation/config/store/groups/groups-slice';
import { setLoadingGroup } from 'presentation/config/store/load-filter/loadingSlice';
import { loadOrganizationsUser } from 'presentation/config/store/organizations/organizations-slice';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import {
  buildRecurrenceConfig,
  calculateEndDateFromOccurrences,
} from '../utils/build-recurrence-config';
import { toISOWithBrazilTZ } from 'presentation/app/utils/date/convert-date-to-iso-with-brazil-utc';

interface IProps {
  getAllGroupsUser?: GetAllGroupsUserUseCaseInterface;
  getAllCoursesUser?: GetAllCoursesUserUseCaseInterface;
  getAllOrganizationsUser?: GetAllOrganizationsUserUseCaseInterface;
}

export const usePushNotification = ({
  getAllGroupsUser,
  getAllCoursesUser,
  getAllOrganizationsUser,
}: IProps) => {
  const [loading, setLoading] = useState<STATUSPAGE>();

  const [statusPushNotification, setStatusPushNotification] =
    useState<PushNotificationResponse>();

  const [statusSchedulePushNotification, setStatusSchedulePushNotification] =
    useState<SchedulePushNotificationResponse>();

  const [statusRecurringPushNotification, setStatusRecurringPushNotification] =
    useState<RecurringPushNotificationResponse>();

  const [showAlert, setShowAlert] = useState<boolean>(false);

  const dispatch = useDispatch();
  const { pushNotificationService } = useService();

  const initialPeriod = useMemo(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (now.getHours() >= 17) {
      tomorrow.setHours(9, 0, 0, 0);
      return {
        startDatePushNotification: tomorrow,
        endDatePushNotification: tomorrow,
      };
    }

    const oneHourLater = new Date(now);
    oneHourLater.setHours(oneHourLater.getHours() + 1, 0, 0, 0);

    return {
      startDatePushNotification: oneHourLater,
      endDatePushNotification: oneHourLater,
    };
  }, []);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: null,
      message: null,
      scheduledTo: initialPeriod.startDatePushNotification,
      group: null,
      course: null,
      profile: null,
      entity: null,
      schedulePushNotification: false,
      recurrencePushNotification: {
        value: RECURRENCE_TYPE.DONT_REPEAT,
        label: getPtBrRecurrenceType(RECURRENCE_TYPE.DONT_REPEAT),
      },
      customRecurrenceInterval: 1,
      customRecurrenceUnit: {
        value: RECURRENCE_TYPE.DAILY,
        label: getPtBrRecurrenceType(RECURRENCE_TYPE.DAILY),
      },
      endAtPushNotification: 'date',
      endAtDate: null,
      endAtOccurrences: 1,
      weeklyDays: [],
    },
    validationSchema: Yup.object().shape({
      course: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),

      entity: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),

      group: Yup.array()
        .nullable()
        .of(
          Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
          }),
        ),

      title: Yup.string()
        .max(50, 'O campo deve ter no máximo 50 caracteres.')
        .required('Campo é obrigatório.'),
      message: Yup.string().required('Campo é obrigatório.'),

      schedulePushNotification: Yup.boolean(),

      recurrencePushNotification: Yup.object()
        .shape({
          value: Yup.number().required(),
          label: Yup.string().required(),
        })
        .nullable(),

      customRecurrenceInterval: Yup.number()
        .min(1, 'Intervalo deve ser pelo menos 1')
        .max(365, 'Intervalo máximo é 365')
        .when(['schedulePushNotification', 'recurrencePushNotification'], {
          is: (schedule, recurrence) =>
            schedule === true && recurrence?.value === RECURRENCE_TYPE.CUSTOM,
          then: (schema) =>
            schema.required(
              'Campo é obrigatório para recorrência personalizada',
            ),
          otherwise: (schema) => schema.nullable(),
        }),

      customRecurrenceUnit: Yup.object()
        .shape({
          value: Yup.number().required(),
          label: Yup.string().required(),
        })
        .when(['schedulePushNotification', 'recurrencePushNotification'], {
          is: (schedule, recurrence) =>
            schedule === true && recurrence?.value === RECURRENCE_TYPE.CUSTOM,
          then: (schema) =>
            schema.required(
              'Campo é obrigatório para recorrência personalizada',
            ),
          otherwise: (schema) => schema.nullable(),
        }),

      weeklyDays: Yup.array()
        .of(Yup.number())
        .when(
          [
            'schedulePushNotification',
            'recurrencePushNotification',
            'customRecurrenceUnit',
          ],
          {
            is: (schedule, recurrence, customUnit) =>
              schedule === true &&
              (recurrence?.value === RECURRENCE_TYPE.WEEKLY ||
                recurrence?.value === RECURRENCE_TYPE.DAYS_OF_WEEK ||
                (recurrence?.value === RECURRENCE_TYPE.CUSTOM &&
                  customUnit?.value === RECURRENCE_TYPE.WEEKLY)),
            then: (schema) =>
              schema
                .min(1, 'Selecione pelo menos um dia da semana')
                .required('Dias da semana são obrigatórios'),
            otherwise: (schema) => schema.nullable(),
          },
        ),

      endAtPushNotification: Yup.string()
        .oneOf(['date', 'occurrences'], 'Valor inválido')
        .when(['schedulePushNotification', 'recurrencePushNotification'], {
          is: (schedule, recurrence) =>
            schedule === true &&
            (recurrence?.value === RECURRENCE_TYPE.CUSTOM ||
              (recurrence?.value !== undefined &&
                recurrence?.value !== null &&
                recurrence?.value !== RECURRENCE_TYPE.DONT_REPEAT)),
          then: (schema) =>
            schema.required('Campo é obrigatório para recorrências'),
          otherwise: (schema) => schema.nullable(),
        }),

      endAtDate: Yup.date().when(
        [
          'schedulePushNotification',
          'recurrencePushNotification',
          'endAtPushNotification',
        ],
        {
          is: (schedule, recurrence, endAt) =>
            schedule === true &&
            (recurrence?.value === RECURRENCE_TYPE.CUSTOM ||
              (recurrence?.value !== undefined &&
                recurrence?.value !== null &&
                recurrence?.value !== RECURRENCE_TYPE.DONT_REPEAT)) &&
            endAt === 'date',
          then: (schema) =>
            schema.required('Data é obrigatória quando "Em" está selecionado'),
          otherwise: (schema) => schema.nullable(),
        },
      ),

      endAtOccurrences: Yup.number()
        .min(1, 'Número de ocorrências deve ser pelo menos 1')
        .max(999, 'Número máximo de ocorrências é 999')
        .when(
          [
            'schedulePushNotification',
            'recurrencePushNotification',
            'endAtPushNotification',
          ],
          {
            is: (schedule, recurrence, endAt) =>
              schedule === true &&
              (recurrence?.value === RECURRENCE_TYPE.CUSTOM ||
                (recurrence?.value !== undefined &&
                  recurrence?.value !== null &&
                  recurrence?.value !== RECURRENCE_TYPE.DONT_REPEAT)) &&
              endAt === 'occurrences',
            then: (schema) =>
              schema.required(
                'Número de ocorrências é obrigatório quando "Após" está selecionado',
              ),
            otherwise: (schema) => schema.nullable(),
          },
        ),

      periodPushNotification: Yup.object().nullable(),
      timeSchedulePushNotification: Yup.string().nullable(),

      profile: Yup.array()
        .of(
          Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
          }),
        )
        .required('Campo é obrigatório.'),
    }),
    onSubmit: (values) => {
      if (
        values.schedulePushNotification &&
        values.recurrencePushNotification?.value !== undefined &&
        values.recurrencePushNotification?.value !== null &&
        values.recurrencePushNotification?.value !== RECURRENCE_TYPE.DONT_REPEAT
      ) {
        const recurrenceType =
          values.recurrencePushNotification.value === RECURRENCE_TYPE.CUSTOM
            ? values.customRecurrenceUnit?.value || RECURRENCE_TYPE.DAILY
            : values.recurrencePushNotification.value ===
                RECURRENCE_TYPE.DAYS_OF_WEEK
              ? RECURRENCE_TYPE.WEEKLY
              : values.recurrencePushNotification.value;

        let endDate: string | undefined;

        if (values.endAtPushNotification === 'date' && values.endAtDate) {
          endDate = toISOWithBrazilTZ(values.endAtDate);
        } else if (
          values.endAtPushNotification === 'occurrences' &&
          values.endAtOccurrences
        ) {
          const calculatedEndDate = calculateEndDateFromOccurrences(
            values.scheduledTo,
            values.endAtOccurrences,
            recurrenceType,
            values.customRecurrenceInterval,
            values.customRecurrenceUnit?.value,
          );
          endDate = toISOWithBrazilTZ(calculatedEndDate);
        }

        return requestRecurrentPushNotification({
          title: values.title,
          message: values.message,
          recurrenceType,
          recurrenceConfig: buildRecurrenceConfig(values),
          startDate: toISOWithBrazilTZ(values.scheduledTo),
          endDate,
          organizationIds: values.entity?.value
            ? [values.entity.value]
            : undefined,
          courseIds: values.course?.value ? [values.course.value] : undefined,
          profileIds: values.profile?.map((item) => item.value) || [],
          groupIds: values.group?.map((item) => item.value) || [],
        });
      }

      if (
        values.schedulePushNotification &&
        values.recurrencePushNotification?.value === RECURRENCE_TYPE.DONT_REPEAT
      ) {
        return requestSchedulingPushNotification({
          title: values.title,
          message: values.message,
          scheduledTo: toISOWithBrazilTZ(values.scheduledTo),
          organizationIds: values.entity?.value
            ? [values.entity.value]
            : undefined,
          courseIds: values.course?.value ? [values.course.value] : undefined,
          profileIds: values.profile?.map((item) => item.value) || [],
          groupIds: values.group?.map((item) => item.value) || [],
        });
      } else {
        requestPushNotification({
          entity: values.entity?.value,
          course: values.course?.value,
          groups: values.group?.map((item) => item.value) || [],
          profiles: values.profile?.map((item) => item.value) || [],
          title: values.title,
          content: values.message,
        });
      }
    },
  });

  const requestGetAllGroupsUser = useCallback(
    (params: GetAllGroupsUserUseCaseInterface.Params) => {
      dispatch(setLoadingGroup(true));

      getAllGroupsUser
        .execute(params)
        .then((res) => dispatch(loadGroupsUser(res)))
        .catch((err) => console.log(err))
        .catch(() => {})
        .finally(() => {
          dispatch(setLoadingGroup(false));
        });
    },
    [],
  );

  const requestGetAllCoursesUser = useCallback((entityId?: string) => {
    dispatch(setLoadingCoursesUser(true));

    getAllCoursesUser
      .execute({ entityId })
      .then((res) => dispatch(loadCoursesUser(res)))
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        dispatch(setLoadingCoursesUser(false));
      });
  }, []);

  const requestGetAllOrganizationsUser = useCallback(
    (params?: GetAllOrganizationsUserParams) => {
      getAllOrganizationsUser
        .execute({ course: params?.course })
        .then((res) => dispatch(loadOrganizationsUser(res)))
        .catch((err) => console.log(err));
    },
    [],
  );

  const requestPushNotification = useCallback(
    (params: PushNotificationUseCaseInterface.Params) => {
      setLoading(STATUSPAGE.LOADING);
      pushNotificationService
        .pushNotification(params)
        .then((res) => {
          setStatusPushNotification(res);
          setShowAlert(true);
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao enviar notificação');
        })
        .finally(() => {
          setLoading(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestSchedulingPushNotification = useCallback(
    (params: SchedulePushNotificationUseCaseInterface.Params) => {
      setLoading(STATUSPAGE.LOADING);
      pushNotificationService
        .schedulePushNotification(params)
        .then((res) => {
          setStatusSchedulePushNotification(res);
          setShowAlert(true);
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao agendar notificação');
        })
        .finally(() => {
          setLoading(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestRecurrentPushNotification = useCallback(
    (params: RecurringPushNotificationUseCaseInterface.Params) => {
      setLoading(STATUSPAGE.LOADING);
      pushNotificationService
        .recurringPushNotification(params)
        .then((res) => {
          setStatusRecurringPushNotification(res);
          setShowAlert(true);
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao criar notificação recorrente');
        })
        .finally(() => {
          setLoading(STATUSPAGE.DONE);
        });
    },
    [],
  );

  return {
    loading,
    validation,
    showAlert,
    setShowAlert,
    statusPushNotification,
    statusSchedulePushNotification,
    statusRecurringPushNotification,
    requestPushNotification,
    requestSchedulingPushNotification,
    requestRecurrentPushNotification,
    requestGetAllGroupsUser,
    requestGetAllCoursesUser,
    requestGetAllOrganizationsUser,
  };
};
